import OneSignal from 'react-onesignal';
import { trackNotificationPermission, trackNotificationInteraction } from './analytics';
import { getAuth } from 'firebase/auth';

let initializationPromise = null;
const isDevelopment = window.location.hostname === 'localhost';

// Mock implementations for development
const developmentMock = {
  init: async () => {
    console.log('OneSignal mock initialized for development');
    return Promise.resolve();
  },
  showSlidedownPrompt: async () => {
    console.log('Mock notification prompt shown');
    return Promise.resolve('granted');
  },
  isPushNotificationsSupported: () => true,
  setSubscription: async () => true,
  getNotificationPermission: async () => 'granted',
  setExternalUserId: async () => true
};

// Initialize OneSignal
export const initOneSignal = async () => {
  if (initializationPromise) {
    return initializationPromise;
  }

  // Use mock implementation in development
  if (isDevelopment) {
    console.log('Using mock OneSignal implementation in development mode');
    initializationPromise = developmentMock.init();
    return initializationPromise;
  }

  initializationPromise = new Promise(async (resolve, reject) => {
    try {
      await OneSignal.init({
        appId: "2104db6a-8fbe-41b7-92d4-570c676f8018",
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        subdomainName: "proverbsdaily",
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: "push",
                autoPrompt: false,
                text: {
                  actionMessage: "Would you like to receive daily proverb notifications?",
                  acceptButton: "Allow",
                  cancelButton: "Not Now"
                }
              }
            ]
          }
        }
      });

      // Set external user ID if logged in
      const auth = getAuth();
      if (auth.currentUser) {
        await OneSignal.setExternalUserId(auth.currentUser.uid);
      }

      console.log('OneSignal initialized successfully');
      resolve();
    } catch (error) {
      console.error('Error initializing OneSignal:', error);
      initializationPromise = null;
      reject(error);
    }
  });

  return initializationPromise;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  try {
    await initOneSignal();
    const permission = await (isDevelopment ? developmentMock : OneSignal).showSlidedownPrompt();
    trackNotificationPermission(permission);
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Subscribe to notifications
export const subscribeToNotifications = async () => {
  try {
    await initOneSignal();
    const implementation = isDevelopment ? developmentMock : OneSignal;
    const isSupported = await implementation.isPushNotificationsSupported();
    if (isSupported) {
      await implementation.setSubscription(true);
      trackNotificationInteraction('push', 'subscribe');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    return false;
  }
};

// Unsubscribe from notifications
export const unsubscribeFromNotifications = async () => {
  try {
    await initOneSignal();
    await (isDevelopment ? developmentMock : OneSignal).setSubscription(false);
    trackNotificationInteraction('push', 'unsubscribe');
    return true;
  } catch (error) {
    console.error('Error unsubscribing from notifications:', error);
    return false;
  }
};

// Get notification subscription status
export const getNotificationStatus = async () => {
  try {
    await initOneSignal();
    const implementation = isDevelopment ? developmentMock : OneSignal;
    const isSupported = await implementation.isPushNotificationsSupported();
    const permission = await implementation.getNotificationPermission();
    return {
      isSubscribed: isSupported,
      permission
    };
  } catch (error) {
    console.error('Error getting notification status:', error);
    return {
      isSubscribed: false,
      permission: 'denied'
    };
  }
}; 